import { useApi, PaginatedResponse } from '@/api';
import { TableCellBottom, Asset, Link } from '@/components';
import {
  CurrencyValue,
  TableSkeleton,
  PercentageValue,
  DataTable,
  Flex,
  Heading,
  InfoCircleIcon,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useTableProps } from '@blockanalitica/ui';
import { useParams } from 'react-router';

type ParamsData = {
  underlying_address: string;
  underlying_name: string;
  underlying_symbol: string;
  ltv: string;
  liquidation_threshold: string;
  liquidation_bonus: string;
  supply_cap: string | null;
  supply_cap_usd: string;
  supply_cap_utilization: string | null;
  borrow_cap: string | null;
  borrow_cap_usd: string;
  borrow_cap_utilization: string | null;
  max_supply_cap: string | null;
  supply_cap_gap: string | null;
  supply_cap_cooldown: number | null;
  max_borrow_cap: string | null;
  borrow_cap_gap: string | null;
  borrow_cap_cooldown: number | null;
  reserve_factor: string;
};

// ReserveConfiguration.MAX_VALID_SUPPLY_CAP / MAX_VALID_BORROW_CAP (uint36 max). Spark sets
// the automator's max to this value when it does not want an onchain ceiling.
const UNLIMITED_CAP = 68719476735;

function formatCooldown(seconds: number): string {
  if (seconds % 86400 === 0) {
    return `${seconds / 86400}d`;
  }
  if (seconds % 3600 === 0) {
    return `${seconds / 3600}h`;
  }
  return `${Math.round(seconds / 60)}m`;
}

function isUnlimited(max: string): boolean {
  return Number(max) >= UNLIMITED_CAP;
}

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1
});

export default function MarketParamsTable() {
  const { network } = useParams() as { network: string };

  const { sorting } = useTableProps({
    pageSize: 15,
    initialSorting: '-ltv'
  });
  const [data, error] = useApi<PaginatedResponse<ParamsData[]>>({
    url: '/params/',
    queryParams: {
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn<ParamsData>[] = [
    {
      header: 'Market',
      getCell: (row) => (
        <Link to={`/markets/${row.underlying_address}`}>
          <Asset
            symbol={row.underlying_symbol}
            address={row.underlying_address}
            network={network}
            name={row.underlying_name}
            iconSize="3"
          />
        </Link>
      ),
      cellAlign: 'flex-start',
      headerAlign: 'flex-start'
    },
    {
      header: 'LTV',
      getCell: (row) => <PercentageValue value={row.ltv} />,
      sortingField: 'ltv',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Threshold',
      getCell: (row) => <PercentageValue value={row.liquidation_threshold} />,
      sortingField: 'liquidation_threshold',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Bonus',
      getCell: (row) => <PercentageValue value={row.liquidation_bonus} />,
      sortingField: 'liquidation_bonus',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Reserve Factor',
      getCell: (row) => <PercentageValue value={row.reserve_factor} />,
      sortingField: 'reserve_factor',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Supply Cap',
      getCell: (row) =>
        row.supply_cap != null ? (
          <CurrencyValue
            value={row.supply_cap}
            options={{
              currencySize: '-1',
              currency: {
                name: row.underlying_symbol,
                address: row.underlying_address,
                network: network
              }
            }}
          />
        ) : (
          '-'
        ),
      getCellBottom: (row) =>
        row.supply_cap != null ? (
          <TableCellBottom checkValue={row.supply_cap_usd}>
            <Flex gap="xs" alignItems="center" style={{ whiteSpace: 'nowrap' }}>
              <CurrencyValue
                value={row.supply_cap_usd}
                options={{
                  currency: 'USD'
                }}
              />
              {row.supply_cap_utilization != null ? (
                <>
                  · <PercentageValue value={row.supply_cap_utilization} /> used
                </>
              ) : null}
            </Flex>
          </TableCellBottom>
        ) : null,
      sortingField: 'supply_cap_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Borrow Cap',

      getCell: (row) =>
        row.borrow_cap != null ? (
          <CurrencyValue
            value={row.borrow_cap}
            options={{
              currencySize: '-1',
              currency: {
                name: row.underlying_symbol,
                address: row.underlying_address,
                network: network
              }
            }}
          />
        ) : (
          '-'
        ),
      getCellBottom: (row) =>
        row.borrow_cap != null ? (
          <TableCellBottom checkValue={row.borrow_cap_usd}>
            <Flex gap="xs" alignItems="center" style={{ whiteSpace: 'nowrap' }}>
              <CurrencyValue
                value={row.borrow_cap_usd}
                options={{
                  currency: 'USD'
                }}
              />
              {row.borrow_cap_utilization != null ? (
                <>
                  · <PercentageValue value={row.borrow_cap_utilization} /> used
                </>
              ) : null}
            </Flex>
          </TableCellBottom>
        ) : null,
      sortingField: 'borrow_cap_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: (
        <Flex gap="xs" alignItems="center">
          <span style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
            Supply Cap
            <br />
            Automator
          </span>
          <Tooltip>
            <TooltipTrigger>
              <InfoCircleIcon size={14} />
            </TooltipTrigger>
            <TooltipContent maxWidth="320px">
              <Flex flexDirection="column" gap="xs">
                <div>
                  <strong>gap</strong>: on each update the cap is set to current
                  supply + gap.
                </div>
                <div>
                  <strong>cooldown</strong>: minimum time between two cap
                  increases.
                </div>
                <div>
                  <strong>max</strong>: upper bound for the cap.
                </div>
              </Flex>
            </TooltipContent>
          </Tooltip>
        </Flex>
      ),
      getCell: (row) =>
        row.supply_cap != null &&
        row.supply_cap_gap != null &&
        row.supply_cap_cooldown != null ? (
          <span style={{ whiteSpace: 'nowrap' }}>
            +{compact.format(Number(row.supply_cap_gap))} /{' '}
            {formatCooldown(row.supply_cap_cooldown)}
          </span>
        ) : (
          '-'
        ),
      getCellBottom: (row) =>
        row.supply_cap != null && row.max_supply_cap != null ? (
          <TableCellBottom>
            <span style={{ whiteSpace: 'nowrap' }}>
              max{' '}
              {isUnlimited(row.max_supply_cap)
                ? 'unlimited'
                : compact.format(Number(row.max_supply_cap))}
            </span>
          </TableCellBottom>
        ) : null,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: (
        <Flex gap="xs" alignItems="center">
          <span style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
            Borrow Cap
            <br />
            Automator
          </span>
          <Tooltip>
            <TooltipTrigger>
              <InfoCircleIcon size={14} />
            </TooltipTrigger>
            <TooltipContent maxWidth="320px">
              <Flex flexDirection="column" gap="xs">
                <div>
                  <strong>gap</strong>: on each update the cap is set to current
                  borrow + gap.
                </div>
                <div>
                  <strong>cooldown</strong>: minimum time between two cap
                  increases.
                </div>
                <div>
                  <strong>max</strong>: upper bound for the cap.
                </div>
              </Flex>
            </TooltipContent>
          </Tooltip>
        </Flex>
      ),
      getCell: (row) =>
        row.borrow_cap != null &&
        row.borrow_cap_gap != null &&
        row.borrow_cap_cooldown != null ? (
          <span style={{ whiteSpace: 'nowrap' }}>
            +{compact.format(Number(row.borrow_cap_gap))} /{' '}
            {formatCooldown(row.borrow_cap_cooldown)}
          </span>
        ) : (
          '-'
        ),
      getCellBottom: (row) =>
        row.borrow_cap != null && row.max_borrow_cap != null ? (
          <TableCellBottom>
            <span style={{ whiteSpace: 'nowrap' }}>
              max{' '}
              {isUnlimited(row.max_borrow_cap)
                ? 'unlimited'
                : compact.format(Number(row.max_borrow_cap))}
            </span>
          </TableCellBottom>
        ) : null,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Flex flexDirection="column" gap="s">
      <Heading size="3">Current Parameters</Heading>
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.underlying_address}
        sorting={sorting}
      />
    </Flex>
  );
}
